import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  QueryBuilder,
  FilterBar,
  isBooleanLike,
  isNumberLike,
  parseQuery,
  useQueryBuilderContext,
} from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { ParsedToken } from '@gonfalon/dataviz-experimental/QueryBuilder';
import type { FieldOptions } from '@gonfalon/dataviz-experimental/types';
import { getContextKindsByProjectKey } from '@gonfalon/openapi/operations/getContextKindsByProjectKey';
import { getSuggestions, type GetSuggestionsRequestBody } from '@gonfalon/openapi/operations/getSuggestions';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { FieldGroup, Label } from '@launchpad-ui/components';
import { useQueries, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { EventFilterInfoTooltip } from '../components/EventFilterInfoTooltip';

import { hasContextKindConflict, selectedContextKindsFromTokens } from './eventQueryConversion';

import styles from './MetricEventQueryBuilder.module.css';

// Custom-event filter bar: feeds getSuggestions into FilterBar, hands defaultValue/onChange to
// EventQueryField (which converts to/from the stored MetricFilter). Field names shown verbatim.

// Field names stay raw; eventQueryConversion.ts maps the prefixes to the stored filter.
const CONTEXT_ATTRS_PREFIX = 'context_attributes_json.';
const DATA_JSON_PREFIX = 'data_json.';

// Display-only label: strip the category prefix (`context_attributes_json.` / `data_json.`) shown in
// the bar and autocomplete. The raw `field` keeps its prefix for conversion — safe because each bar
// is already scoped to one category, so the prefix isn't needed to disambiguate on screen.
function displayFieldName(field: string): string {
  if (field.startsWith(CONTEXT_ATTRS_PREFIX)) {
    return field.slice(CONTEXT_ATTRS_PREFIX.length);
  }
  if (field.startsWith(DATA_JSON_PREFIX)) {
    return field.slice(DATA_JSON_PREFIX.length);
  }
  return field;
}

// Synthetic scope-only field (Option A): picking kinds here narrows the offered context attributes.
// Never persisted — eventQueryConversion drops it going in and re-derives it from stored clauses out.
const CONTEXT_KIND_FIELD = 'context';

// Shown on the context bar when a filter targets a context kind other than the pinned one.
const CONTEXT_KIND_CONFLICT_MESSAGE = 'Filter conflicting with pinned context kind';

// The context bar owns the `context` scope picker and every context-attribute field; the event bar
// owns the rest. Partitions the shared FieldOptions into the two bars.
function isContextField(field: string): boolean {
  return field === CONTEXT_KIND_FIELD || field.startsWith(CONTEXT_ATTRS_PREFIX);
}

// Display-only autocomplete section label (the bar groups/sorts sections by this string).
function fieldCategoryFor(field: string): string {
  return field.startsWith(CONTEXT_ATTRS_PREFIX) ? 'Context attributes' : 'Event properties';
}

// Prefixed context-attribute fields referenced by committed filter tokens (`context_attributes_json.*`).
// Used to keep those field options available for display even when scoping would drop them, so their
// lozenge resolves its trimmed `displayValue` instead of showing the raw prefixed field.
function committedContextAttributeFields(tokens: ParsedToken[]): string[] {
  const fields: string[] = [];
  const walk = (list: ParsedToken[]) => {
    for (const token of list) {
      if (token.type === 'FILTER' && token.field.startsWith(CONTEXT_ATTRS_PREFIX)) {
        fields.push(token.field);
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(tokens);
  return fields;
}

// Display-only field options synthesized from a seeded query's filter tokens. Renders each committed
// lozenge with its trimmed field name (`version`) instead of the raw prefixed field (`data_json.version`)
// on the very first paint — before the async getSuggestions fetch resolves. Superseded by the fetched
// options once they arrive (they are listed first, so LozengeFilter's field lookup prefers them). The
// `context` scope token is skipped since it's never rendered as a filter lozenge.
function seedFieldOptions(query?: string): FieldOptions[] {
  if (!query) {
    return [];
  }
  const seen = new Set<string>();
  const options: FieldOptions[] = [];
  const walk = (tokens: ParsedToken[]) => {
    for (const token of tokens) {
      if (token.type === 'FILTER' && token.field !== CONTEXT_KIND_FIELD) {
        if (!seen.has(token.field)) {
          seen.add(token.field);
          options.push({
            field: token.field,
            displayValue: displayFieldName(token.field),
            fieldCategory: fieldCategoryFor(token.field),
            allowedOperators: ['=', '!='],
            sampleValues: [],
            allowCustom: true,
          });
        }
      } else if (token.type === 'EXPRESSION') {
        walk(token.inner);
      }
    }
  };
  walk(parseQuery(query));
  return options;
}

// Appends the seed options for any field not already covered by the fetched suggestions. Fetched
// options stay first so LozengeFilter (which resolves a lozenge via the first matching field) keeps
// their richer metadata once loaded; seeds only fill the gap on first render / for fields the
// suggestions omit.
function mergeSeedFieldOptions(fetched: FieldOptions[], seeds: FieldOptions[]): FieldOptions[] {
  if (seeds.length === 0) {
    return fetched;
  }
  const present = new Set(fetched.map((option) => option.field));
  const extra = seeds.filter((seed) => !present.has(seed.field));
  return extra.length > 0 ? [...fetched, ...extra] : fetched;
}

// Narrows offered options to the selected kinds' context attributes; event properties and the
// `context` option are always kept. No kinds selected → everything returned unchanged.
function scopeFieldOptions(fieldOptions: FieldOptions[], selectedKinds: string[]): FieldOptions[] {
  if (selectedKinds.length === 0) {
    return fieldOptions;
  }
  const kinds = new Set(selectedKinds);
  return fieldOptions.filter((option) => {
    if (option.field === CONTEXT_KIND_FIELD) {
      return true;
    }
    if (option.field.startsWith(CONTEXT_ATTRS_PREFIX)) {
      const kind = option.field.slice(CONTEXT_ATTRS_PREFIX.length).split('.')[0];
      return kinds.has(kind);
    }
    return true;
  });
}

// Element type of the getSuggestions response items, inferred so the mapper stays in sync with the
// operation without importing a generated alias.
type SuggestionItem = NonNullable<NonNullable<Awaited<ReturnType<typeof getSuggestions>>['data']>['items']>[number];

// getSuggestions request for custom SDK events over the last 7 days. `contextKind`, when set, scopes
// attribute suggestions to that kind (a `suggestFields` option). NOTE: the suggestion endpoint does
// NOT accept a top-level `filters` field (verified: any `filters` shape → 400 "could not be
// deserialized"), so suggestions can't be scoped to a specific event key — they're project-wide.
function buildSuggestionsPayload(
  projectKey: string,
  from: number,
  to: number,
  contextKind?: string,
): GetSuggestionsRequestBody {
  return {
    projectKey,
    dataSource: {
      // SDK custom events live under domain 'sdk' / kind 'custom_event'.
      domain: 'sdk',
      kind: 'custom_event',
      timeRange: { from, to, timezone: 'America/New_York' },
    },
    suggestFields: {
      limit: 100, // max
      ...(contextKind ? { contextKind } : {}),
      withValues: { limit: 100 },
    },
  };
}

// Maps getSuggestions fields (event properties + context attributes) to FilterBar options, verbatim
// (number/boolean/string classification; the offered operators widen by type). Drops kind-only
// context entries and duplicate field names. Shared by the base fetch and the per-kind scoped fetch.
function mapSuggestionsToFieldOptions(items: SuggestionItem[]): FieldOptions[] {
  const seenFields = new Set<string>();

  return (
    items
      .map((item): FieldOptions => {
        const sampleValues = item.sampleValues ?? [];
        // Dedupe values — react-aria keys options by value and crashes on duplicates.
        const seenSampleValues = new Set<string>();
        const mappedSampleValues = sampleValues.flatMap((sv) => {
          const value = String(sv.value);
          if (seenSampleValues.has(value)) {
            return [];
          }
          seenSampleValues.add(value);
          return [{ value, approxCount: sv.count }];
        });

        // Field name used verbatim (not trimmed).
        const field = item.field;
        const fieldCategory = fieldCategoryFor(field);

        const hasSamples = sampleValues.length > 0;
        const allNumber = hasSamples && sampleValues.every((sv) => isNumberLike(sv.value));
        const allBoolean = hasSamples && sampleValues.every((sv) => isBooleanLike(sv.value));
        const allString = hasSamples && sampleValues.every((sv) => typeof sv.value === 'string');

        if (allNumber) {
          return {
            field,
            fieldCategory,
            valueType: 'number',
            // Equality + numeric comparison (see mapBarOp). The comparison ops are single-value.
            allowedOperators: ['=', '!=', '>', '>=', '<', '<='],
            sampleValues: mappedSampleValues,
            allowCustom: true,
          } satisfies FieldOptions;
        } else if (allBoolean) {
          return {
            field,
            fieldCategory,
            valueType: 'boolean',
            allowedOperators: ['=', '!='],
            sampleValues: mappedSampleValues,
            allowCustom: true,
          } satisfies FieldOptions;
        } else if (allString) {
          return {
            field,
            fieldCategory,
            valueType: 'string',
            multi: true,
            // Equality, regex, and string set-membership (contains/starts/ends) — see mapBarOp.
            allowedOperators: ['=', '!=', '//', '!//', '**', '^=', '=^'],
            sampleValues: mappedSampleValues,
            allowCustom: true,
          } satisfies FieldOptions;
        }
        // Untyped field (no/mixed samples): equality only — offering more would surface operators
        // mapBarOp can't map (silently dropped on save).
        return {
          field,
          fieldCategory,
          allowedOperators: ['=', '!='],
          sampleValues: mappedSampleValues,
          allowCustom: true,
        } satisfies FieldOptions;
      })
      .filter((option) => {
        // Drop kind-only context entries (`context_attributes_json.<kind>` with no attribute): a
        // context filter needs both a kind and an attribute, so these can't form a saveable filter.
        if (
          option.field.startsWith(CONTEXT_ATTRS_PREFIX) &&
          !option.field.slice(CONTEXT_ATTRS_PREFIX.length).includes('.')
        ) {
          return false;
        }
        // Drop exact-duplicate field names (react-aria duplicate-key guard); nothing else filtered.
        if (seenFields.has(option.field)) {
          return false;
        }
        seenFields.add(option.field);
        return true;
      })
      // Trim the category prefix for display only; `field` stays prefixed for conversion.
      .map((option) => ({ ...option, displayValue: displayFieldName(option.field) }))
  );
}

// Single project-wide suggestion fetch shared by both bars. The response carries both categories
// (`data_json.*` + `context_attributes_json.*`), which the component splits by prefix. The context
// bar additionally fetches per pinned kind for depth. Appends the synthetic `context` scope picker.
function useEventFieldOptions(projectKey: string): FieldOptions[] {
  const [now] = useState(() => Date.now());
  const sevenDaysAgo = now - 604_800_000;
  // Context kinds for the synthetic `context` field, fetched from the OpenAPI op so the metric form owns it.
  const { data: contextKindsData } = useQuery({
    queryFn: () => getContextKindsByProjectKey({ projectKey }),
    queryKey: ['contextKinds', projectKey],
    enabled: !!projectKey,
  });
  const { data } = useQuery({
    queryFn: () => getSuggestions({ body: buildSuggestionsPayload(projectKey, sevenDaysAgo, now) }),
    queryKey: ['suggestions', 'sdk', 'custom_event', projectKey],
    enabled: !!projectKey,
  });

  return useMemo<FieldOptions[]>(() => {
    const kept = mapSuggestionsToFieldOptions(data?.data?.items ?? []);

    // Add the scope-only single-value `context` picker (Option A) only when there are unarchived kinds to choose from.
    const unhiddenContextKinds = (contextKindsData?.data?.items ?? []).filter((kind) => !kind.archived);
    if (unhiddenContextKinds.length > 0) {
      kept.push({
        field: CONTEXT_KIND_FIELD,
        // "Context" sorts before "Context attributes"/"Event properties" so the picker renders at the top.
        fieldCategory: 'Context',
        valueType: 'string',
        // Single-value: one kind per `context` token so the popover only allows one selection at a time.
        supportsMultipleValues: false,
        allowedOperators: ['=', '!='],
        allowCustom: false,
        sampleValues: unhiddenContextKinds.map((kind) => ({
          value: kind.key,
          approxCount: 0,
          displayValue: kind.name,
        })),
      });
    }

    return kept;
  }, [data, contextKindsData]);
}

// Additional context-attribute fetch per pinned kind (suggestFields.contextKind), so picking
// context=account surfaces that kind's attributes even when they're absent from the initial
// mixed-kind set. One request per selected kind; results merged and deduped by field.
function useContextScopedFieldOptions(projectKey: string, selectedKinds: string[]): FieldOptions[] {
  const [now] = useState(() => Date.now());
  const sevenDaysAgo = now - 604_800_000;
  return useQueries({
    queries: selectedKinds.map((kind) => ({
      queryFn: () => getSuggestions({ body: buildSuggestionsPayload(projectKey, sevenDaysAgo, now, kind) }),
      queryKey: ['suggestions', 'sdk', 'custom_event', 'contextKind', projectKey, kind],
      enabled: !!projectKey,
    })),
    combine: (results) => {
      const seen = new Set<string>();
      const merged: FieldOptions[] = [];
      for (const result of results) {
        for (const option of mapSuggestionsToFieldOptions(result.data?.data?.items ?? [])) {
          // Keep only context attributes — the scoped call exists to widen attribute coverage.
          if (!option.field.startsWith(CONTEXT_ATTRS_PREFIX) || seen.has(option.field)) {
            continue;
          }
          seen.add(option.field);
          merged.push(option);
        }
      }
      return merged;
    },
  });
}

// Context bar: reads the LIVE parsed query (not just the committed tokens) so scoping updates the
// moment a kind is picked, before the value popover commits; then narrows attribute options to those
// kinds (Option A).
const ContextFilterBar: React.FC<{
  isEditable: boolean;
  inputId?: string;
  fieldOptions: FieldOptions[];
  ariaLabelledby?: string;
  isInvalid?: boolean;
}> = ({ isEditable, inputId, fieldOptions, ariaLabelledby, isInvalid }) => {
  const projectKey = useProjectKey();
  const { parsedQuery } = useQueryBuilderContext();
  const selectedKinds = useMemo(() => selectedContextKindsFromTokens(parsedQuery), [parsedQuery]);
  // Additional attributes for the pinned kinds (via suggestFields.contextKind), widening the base set.
  const serverScopedOptions = useContextScopedFieldOptions(projectKey, selectedKinds);
  const scopedFieldOptions = useMemo(() => {
    // Narrow the base set to the selected kinds (keeps the `context` picker), then union in the
    // per-kind server-scoped attributes so kinds missing from the base set still populate.
    const base = scopeFieldOptions(fieldOptions, selectedKinds);
    const seen = new Set(base.map((option) => option.field));
    const merged = [...base, ...serverScopedOptions.filter((option) => !seen.has(option.field))];
    // Re-add any committed context-attribute field that scoping dropped (a filter on a non-pinned
    // kind), so its lozenge still resolves its trimmed displayValue instead of showing the raw prefix.
    const present = new Set(merged.map((option) => option.field));
    const committed = new Set(committedContextAttributeFields(parsedQuery));
    const readd = fieldOptions.filter((option) => committed.has(option.field) && !present.has(option.field));
    return readd.length > 0 ? [...merged, ...readd] : merged;
  }, [fieldOptions, selectedKinds, serverScopedOptions, parsedQuery]);

  return (
    <div className={styles.queryBuilderFilters}>
      <FilterBar
        readonly={!isEditable}
        fieldOptions={scopedFieldOptions}
        inputId={inputId}
        ariaLabelledby={ariaLabelledby}
        isInvalid={isInvalid}
        showSearchIcon={isEditable}
        placeholder=""
      />
    </div>
  );
};

// Event-properties bar: no scope narrowing, so it doesn't read the QueryBuilder context.
const EventFilterBar: React.FC<{
  isEditable: boolean;
  inputId?: string;
  fieldOptions: FieldOptions[];
  ariaLabelledby?: string;
}> = ({ isEditable, inputId, fieldOptions, ariaLabelledby }) => (
  <div className={styles.queryBuilderFilters}>
    <FilterBar
      readonly={!isEditable}
      fieldOptions={fieldOptions}
      inputId={inputId}
      ariaLabelledby={ariaLabelledby}
      showSearchIcon={isEditable}
      placeholder=""
    />
  </div>
);

interface MetricEventQueryBuilderProps {
  contextDefaultValue?: string;
  eventDefaultValue?: string;
  isEditable?: boolean;
  inputId?: string;
  // `editedBar` names the bar the user just touched, so consumers can freeze that bar's seed
  // independently and keep re-seeding the untouched bar from deferred defaultValues.
  onChange?: (bars: {
    contextTokens: ParsedToken[];
    eventTokens: ParsedToken[];
    editedBar: 'context' | 'event';
  }) => void;
  errorMessage?: string;
  className?: string;
}

export const MetricEventQueryBuilder: React.FC<MetricEventQueryBuilderProps> = ({
  contextDefaultValue,
  eventDefaultValue,
  isEditable = true,
  inputId,
  onChange,
  errorMessage,
  className,
}) => {
  const projectKey = useProjectKey();
  // One project-wide fetch, split by prefix into the two bars.
  const fieldOptions = useEventFieldOptions(projectKey);
  // Seed each bar's field options from its default query so committed lozenges render their trimmed
  // field name on the first paint (no `data_json.version` → `version` flash) — critical on the
  // read-only detail page, which relies solely on the async fetch otherwise.
  const contextSeedOptions = useMemo(() => seedFieldOptions(contextDefaultValue), [contextDefaultValue]);
  const eventSeedOptions = useMemo(() => seedFieldOptions(eventDefaultValue), [eventDefaultValue]);
  const contextFieldOptions = useMemo(
    () =>
      mergeSeedFieldOptions(
        fieldOptions.filter((o) => isContextField(o.field)),
        contextSeedOptions,
      ),
    [fieldOptions, contextSeedOptions],
  );
  const eventFieldOptions = useMemo(
    () =>
      mergeSeedFieldOptions(
        fieldOptions.filter((o) => !isContextField(o.field)),
        eventSeedOptions,
      ),
    [fieldOptions, eventSeedOptions],
  );

  const contextInputId = inputId ? `${inputId}-context` : 'context';
  const eventInputId = inputId ? `${inputId}-event` : 'event';
  // Label ids name each bar's textbox via aria-labelledby (the focusable element is a role="textbox"
  // div, not a labelable form element, so a `<label htmlFor>` can't associate with it).
  const contextLabelId = `${contextInputId}-label`;
  const eventLabelId = `${eventInputId}-label`;

  // Context-attribute filter targeting a kind other than the pinned one (see hasContextKindConflict).
  // The `context` pin is derived from the stored filter on load, so a conflict only arises from a user
  // edit — which fires the QueryBuilder onChange below.
  const [contextKindConflict, setContextKindConflict] = useState(false);

  // Each bar owns its own QueryBuilder, so hold both bars' latest tokens and surface the pair together.
  // QueryBuilder doesn't fire onChange for a seeded defaultValue, so seed each ref from the parsed
  // default. Otherwise editing one bar would merge against an empty array for the other and silently
  // drop that bar's stored filters (e.g. editing event properties on a metric that also has context
  // filters). Each bar's `edited` flag freezes its ref once the user touches it, so a later deferred
  // defaultValue (from a network load) only re-seeds a bar the user hasn't edited yet.
  const contextTokensRef = useRef<ParsedToken[]>(parseQuery(contextDefaultValue ?? ''));
  const eventTokensRef = useRef<ParsedToken[]>(parseQuery(eventDefaultValue ?? ''));
  const contextEditedRef = useRef(false);
  const eventEditedRef = useRef(false);

  useEffect(() => {
    if (!contextEditedRef.current) {
      contextTokensRef.current = parseQuery(contextDefaultValue ?? '');
    }
  }, [contextDefaultValue]);
  useEffect(() => {
    if (!eventEditedRef.current) {
      eventTokensRef.current = parseQuery(eventDefaultValue ?? '');
    }
  }, [eventDefaultValue]);

  const handleChange = useCallback(
    (which: 'context' | 'event', tokens: ParsedToken[]) => {
      if (which === 'context') {
        contextTokensRef.current = tokens;
        contextEditedRef.current = true;
        setContextKindConflict(hasContextKindConflict(tokens));
      } else {
        eventTokensRef.current = tokens;
        eventEditedRef.current = true;
      }
      onChange?.({
        contextTokens: contextTokensRef.current,
        eventTokens: eventTokensRef.current,
        editedBar: which,
      });
    },
    [onChange],
  );

  return (
    <div className={clsx(styles.queryBuilders, className)}>
      <QueryBuilder
        key={contextDefaultValue}
        defaultValue={contextDefaultValue}
        onChange={(event) => handleChange('context', event.tokens)}
      >
        <FieldGroup
          className={styles.filterGroup}
          errorMessage={contextKindConflict ? CONTEXT_KIND_CONFLICT_MESSAGE : undefined}
        >
          {/* The label + explanatory tooltip belong to the editable form; the read-only detail page
           * has its own "Where event is filtered by" heading, so suppress them there. */}
          {isEditable && (
            <div className={styles.filterHeader}>
              <Label id={contextLabelId}>Filter by context attributes</Label>
              <EventFilterInfoTooltip labelText="About context attribute filters">
                Filter the event by an attribute of a context kind e.g. <code>user</code> or <code>account</code>{' '}
                associated with this event.
              </EventFilterInfoTooltip>
            </div>
          )}
          <ContextFilterBar
            isEditable={isEditable}
            inputId={contextInputId}
            ariaLabelledby={isEditable ? contextLabelId : undefined}
            fieldOptions={contextFieldOptions}
            isInvalid={contextKindConflict}
          />
        </FieldGroup>
      </QueryBuilder>
      <QueryBuilder
        key={eventDefaultValue}
        defaultValue={eventDefaultValue}
        onChange={(event) => handleChange('event', event.tokens)}
      >
        <FieldGroup className={styles.filterGroup} errorMessage={errorMessage}>
          {isEditable && (
            <div className={styles.filterHeader}>
              <Label id={eventLabelId}>Filter by event properties</Label>
              <EventFilterInfoTooltip labelText="About event property filters">
                Filter the event by metadata sent in the SDK&apos;s track method e.g.{' '}
                <code>client.track(&apos;eventKey&apos;);</code>
              </EventFilterInfoTooltip>
            </div>
          )}
          <EventFilterBar
            isEditable={isEditable}
            inputId={eventInputId}
            ariaLabelledby={isEditable ? eventLabelId : undefined}
            fieldOptions={eventFieldOptions}
          />
        </FieldGroup>
      </QueryBuilder>
    </div>
  );
};
